import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as CloseFilledIcon } from 'vendor/svg/report/close-filled.svg';
import { ReactComponent as OnlineIcon } from 'vendor/svg/report/online-icon.svg';
import { ReactComponent as PowerpointIcon } from 'vendor/svg/report/powerpoint-icon.svg';
import { ReactComponent as ExportReportIcon } from 'vendor/svg/report/export-icon.svg';
import { State } from "store/types";
import { Online } from "./Form/Online";
import { PowerPoint } from "./Form/PowerPoint";

import { Button } from "@launchmetrics/lm-ui";
import { setReportType } from 'store/article/report/reducers';
import { SidePanel } from "components/common/SidePanel/SidePanel";
import { isCurrentPage } from 'lib/pathCheck';
import { SidePanelContent, SidePanelFooter, SidePanelHeader } from "components/common/SidePanel";


import './ReportConfiguration.scss';
import { SaveReportModal } from "./SaveReportModal/SaveReportModal";

const VALID_REPORT_TYPES = ['online', 'powerpoint'];

export const ReportConfiguration: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [reportType, location] = useSelector((state: State) => [
		state.article.report.configuration.type,
		state.router.location
	]);

	const closeReportSidePanel = useCallback(() => {
		dispatch(setReportType({ type: '' }));
	}, [dispatch]);

	const [reportIcon, reportForm] = useMemo(() => {
		switch (reportType) {
			case 'online': return [<OnlineIcon />, <Online />];
			case 'powerpoint': return [<PowerpointIcon />, <PowerPoint />];
			default: return ['', ''];
		}
	}, [reportType]);

	const showReportConfiguration = useMemo(() => {
		const validRoute = isCurrentPage(location.pathname, 'article');
		if (!reportType || !VALID_REPORT_TYPES.includes(reportType) || !validRoute) {
			closeReportSidePanel();
			return false;
		}
		return true;
	}, [reportType, location, closeReportSidePanel]);

	const header = useMemo(() => (
		<>
			<div>
				<div className='side-panel-header-title'>{reportIcon} {t(`reports.side_panel.header_title.${reportType}`)}</div>
				<div className='side-panel-header-text'>{t(`reports.side_panel.header_text.${reportType}`)}</div>
			</div>
			<div>
				<Button
					data-testid='closeBtn'
					id="closeBtn"
					onClick={() => {
						dispatch({ type:'report/resetReport' });
						closeReportSidePanel();
					}}
					leftIcon={<CloseFilledIcon />}
					variant='textGray' size='small'
				/>
			</div>
		</>
	), [reportIcon, t, reportType, dispatch, closeReportSidePanel]);

	const footer = useMemo(() => (
		<>
			<SaveReportModal />
			<Button onClick={() => {}} variant='primary' size='medium' leftIcon={<ExportReportIcon />}>{t('reports.side_panel.footer.export')}</Button>
		</>
	), [t]);

	return (
		<>
			{showReportConfiguration &&
				<SidePanel>
					<SidePanelHeader>
						{header}
					</SidePanelHeader>

					<SidePanelContent>
						{reportForm}
					</SidePanelContent>

					<SidePanelFooter>
						{footer}
					</SidePanelFooter>
				</SidePanel>
			}
		</>
	);
};
